import { AuthActionCreators, IGet } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';
import { exportToXlsx, SheetConfig } from '@nkr/utils/exportToExcel';
import { useEffect, useState } from 'react';
import useCoachList from './use-coach-list';
import * as moment from 'moment';

interface ICoachPerformanceRow {
    coachContactId: number;
    month: string;
    newAssignments: number;
    totalActivePatients: number;
    activeCustomSites: number;
    activeStarterSites: number;
    newInactive: number;
    livingDonorTransplants: number;
    newMsRegistrations: number;
    newOptIns: number;
    optInPercent: number;
}

export interface IAggregatedMonth {
    key: string;
    year: number;
    month: number;
    newAssignments: number;
    totalActivePatients: number;
    activeCustomSites: number;
    activeStarterSites: number;
    newInactive: number;
    livingDonorTransplants: number;
    newMsRegistrations: number;
    newOptIns: number;
    optInPercent: number;
}

const  aggregateByMonth = (rows: ICoachPerformanceRow[]): IAggregatedMonth[] => {
    const map = rows.reduce((acc, row) => {
        const m = moment(row.month);
        const year = m.year();
        const month = m.month() + 1;
        const key = m.format('YYYY-MM');

        if (!acc.has(key)) {
            acc.set(key, {
                key, year, month,
                newAssignments: 0,
                totalActivePatients: 0,
                activeCustomSites: 0,
                activeStarterSites: 0,
                newInactive: 0,
                livingDonorTransplants: 0,
                newMsRegistrations: row.newMsRegistrations, //same value on every coach row
                newOptIns: row.newOptIns, //same value on every coach row
                optInPercent: 0,
            });
        }

        const agg = acc.get(key);
        agg.newAssignments += row.newAssignments;
        agg.totalActivePatients += row.totalActivePatients;
        agg.activeCustomSites += row.activeCustomSites;
        agg.activeStarterSites += row.activeStarterSites;
        agg.newInactive += row.newInactive;
        agg.livingDonorTransplants += row.livingDonorTransplants;

        return acc;
    }, new Map<string, IAggregatedMonth>());

    return Array.from(map.values())
        .map(agg => ({
            ...agg,
            optInPercent: agg.newMsRegistrations === 0
                ? 0
                : (agg.newOptIns / agg.newMsRegistrations) * 100,
        }))
        .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month);
}

interface IRetVal {
    data: IAggregatedMonth[] | null;
    loading: boolean;
    error: string;
    exportData: () => void;
}

const useCoachPerformanceReport = (isSuperAdmin: boolean, year: number, coachId: number): IRetVal => {
    const { dispatch } = useAppHookContext();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [data, setData] = useState<IAggregatedMonth[] | null>(null);
    const { getCoachName } = useCoachList();

    useEffect(() => {
        setLoading(true);
        setError('');

        const url = isSuperAdmin
            ? `${__API__}/admin/microsites/report?year=${year}&coachContactId=${coachId}`
            : `${__API__}/microsite/coach/report?year=${year}`;

        const config: IGet = { url };

        const fetchData = async () => {
            try {
                const response = await dispatch(AuthActionCreators.apiGet(config));

                if (response && response.data.success) {
                    setData(aggregateByMonth(response.data.results));
                } else {
                    setError('Unable to load coach performance report');
                    setData(null);
                }
            } catch (err) {
                setData(null);
                setError(err?.message || 'An unexpected error occurred');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isSuperAdmin, year, coachId]);

    const exportData = (): void => {
        if (!data) return;

        const coachName = isSuperAdmin ? getCoachName(coachId) : null;

        const columns = [
            { key: 'metric', header: 'Metric' },
            ...data.map(m => ({ key: m.key, header: moment({ year: m.year, month: m.month - 1 }).format('MMM YYYY') })),
        ];

        const makeRow = (label: string, getValue: (m: IAggregatedMonth) => number): Record<string, string | number> => {
            const row: Record<string, string | number> = { metric: label };
            data.forEach(m => { row[m.key] = getValue(m); });
            return row;
        };

        const rows = [
            makeRow('New Assignments', m => m.newAssignments),
            makeRow('Total Active Patients', m => m.totalActivePatients),
            makeRow('Active Custom Sites', m => m.activeCustomSites),
            makeRow('Active Starter Sites', m => m.activeStarterSites),
            makeRow('New Inactive', m => m.newInactive),
            makeRow('Living Donor TX', m => m.livingDonorTransplants),
            ...(isSuperAdmin ? [
                makeRow('New MS Registration', m => m.newMsRegistrations),
                makeRow('Opt-In %', m => m.optInPercent),
            ] : []),
        ];

        const sheet: SheetConfig = { name: 'Coach Performance', columns, data: rows };
        exportToXlsx(`Coach Performance Report${coachName ? ` - ${coachName}` : ''} - ${year}.xlsx`, [sheet]);
    };

    return { data, loading, error, exportData };
}

export default useCoachPerformanceReport;
